import { useState } from "react";

import Box from "@/components/Box";
import { ExternalTextLink } from "@/components/Links";
import { EXPERTS_OVERVIEW_URL } from "@/core/urls";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import LinkWrapper from "@/tpo/LinkWrapper";
import Menu from "@/tpo/Menu";
import BurgerButton from "@/tpo/header/BurgerButton";
import { CloseButton } from "@/v2/Buttons";

import styles from "../common/BurgerMenu.module.css";

const MenuItem = ({ children, onClick, color = "white", ...props }) => (
  <Box
    color={color}
    fontFamily="gilroyMedium"
    fontSize={24}
    lineHeight="150%"
    py={20}
    className={styles.burgerMenuItem}
    cursor="pointer"
    onClick={onClick}
    {...props}
  >
    {children}
  </Box>
);

const ExpandableMenuItem = ({ label, isOpen, onToggle, children, collapsableContainerProps }) => (
  <Box>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      color="white"
      fontFamily="gilroyMedium"
      fontSize={24}
      lineHeight="150%"
      py={20}
      cursor="pointer"
      onClick={onToggle}
      className={styles.burgerMenuExpandableItem}
    >
      <Box color="inherit">{label}</Box>
      <ChevronComponent direction={isOpen ? "top" : "bottom"} width={5} fill="white" />
    </Box>
    {isOpen && (
      <Box py={20} {...collapsableContainerProps}>
        {children}
      </Box>
    )}
  </Box>
);

const SubMenuItem = ({ label, onClick, ...props }) => (
  <MenuItem
    onClick={onClick}
    py={20}
    fontSize={16}
    border={0}
    className={styles.subMenuItem}
    {...props}
  >
    {label}
  </MenuItem>
);

export default function LoggedOutBurgerMenu({ burgerButtonProps, bg: _bg = "dark" }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  const bg = !menuOpen ? _bg : "#282828";

  const handleLinkClick = () => {
    setMenuOpen(false);
  };

  return (
    <Menu
      opened={menuOpen}
      onChange={setMenuOpen}
      trigger="click"
      xOffset={-176}
      fullScreen={[true, true, false]}
    >
      <Menu.Target>
        <BurgerButton
          onClick={() => setMenuOpen(!menuOpen)}
          open={menuOpen}
          {...burgerButtonProps}
          bg={bg}
          borderBottomRightRadius={menuOpen ? 0 : 5}
        />
      </Menu.Target>
      <Menu.Dropdown
        bg={bg}
        borderRadius={10}
        borderTopRightRadius={0}
        minWidth={400}
        maxWidth={600}
        px={40}
        py={40}
        data-component-name="LoggedOutBurgerMenu"
        render={(children, { fullScreen, props }) =>
          fullScreen ? (
            <Box {...props} overflow="auto" borderRadius={0}>
              <Group alignItems="center" justifyContent="flex-end">
                <CloseButton
                  color="white"
                  onClick={() => setMenuOpen(false)}
                  size={null}
                  mr="-4px"
                />
              </Group>
              {children}
            </Box>
          ) : (
            <Box {...props}>{children}</Box>
          )
        }
      >
        <MenuItem onClick={handleLinkClick}>Find a Practitioner</MenuItem>
        <MenuItem
          as={ExternalTextLink}
          href="https://www.regeneruslabs.me/practitioners"
          underline={false}
          display="block"
          target={null}
          rel={null}
        >
          Practitioners
        </MenuItem>
        <MenuItem as={LinkWrapper} to={EXPERTS_OVERVIEW_URL} display="block">
          Patients
        </MenuItem>
        <MenuItem
          as={ExternalTextLink}
          href="https://www.regeneruslabs.com/practitioner-portal"
          underline={false}
          display="block"
          target={null}
          rel={null}
        >
          Platform
        </MenuItem>
        <MenuItem
          as={ExternalTextLink}
          href="https://www.regeneruslabs.com/tests"
          underline={false}
          display="block"
          target={null}
          rel={null}
        >
          Our Tests
        </MenuItem>
        <ExpandableMenuItem
          label="About"
          isOpen={aboutOpen}
          onToggle={() => setAboutOpen(!aboutOpen)}
          collapsableContainerProps={{ pl: 28 }}
        >
          <SubMenuItem
            label="Our Vision"
            as={ExternalTextLink}
            href="https://www.regeneruslabs.com/about"
            underline={false}
            display="block"
            target={null}
            rel={null}
          />
          <SubMenuItem
            label="Contact Us"
            as={ExternalTextLink}
            href="https://www.regeneruslabs.com/contact"
            underline={false}
            display="block"
            target={null}
            rel={null}
          />
        </ExpandableMenuItem>
      </Menu.Dropdown>
    </Menu>
  );
}
